import React, { createContext, useContext, useMemo } from 'react';
import { useLatest } from 'react-use';

import { LogMetadata, Logger } from './main';

export type LoggerArgs = { name: string; metadata?: LogMetadata };

export interface LoggingContextProps {
  children: React.ReactNode;
  loggerInput: Logger | LoggerArgs;
}

type LoggingContextState = {
  logger: Logger;
  extend: (args: LoggerArgs) => Logger;
};

export const LoggingContext = createContext<LoggingContextState | undefined>(
  undefined,
);

export const useLoggingContext = () => {
  const context = useContext(LoggingContext);
  if (!context) {
    throw new Error(
      'useLoggingContext must be used within a LoggingContextProvider',
    );
  }
  return context;
};

export const useLogger = () => {
  const context = useLoggingContext();
  return context.logger;
};

export const useExtendedLogger = (args: LoggerArgs) => {
  const context = useLoggingContext();
  const extended = useMemo(() => context.extend(args), [context, args]);
  return extended;
};

export const useNamedLogger = (name: string) => {
  const context = useLoggingContext();
  const extended = useMemo(() => context.extend({ name }), [context, name]);
  return extended;
};

export const LoggingContextProvider: React.FC<LoggingContextProps> = ({
  children,
  loggerInput,
}) => {
  const isLoggerInput = loggerInput instanceof Logger;
  const existingLoggerContext = useContext(LoggingContext);

  const parentLogger = useLatest(
    existingLoggerContext?.logger?.logger ?? isLoggerInput
      ? (loggerInput as Logger)?.logger
      : undefined,
  );

  const context: LoggingContextState = useMemo(() => {
    const logger = isLoggerInput
      ? loggerInput
      : new Logger(loggerInput.name, {
          metadata: loggerInput.metadata,
          logger: parentLogger.current,
        });

    const extend = ({ name, metadata: meta }: LoggerArgs) => {
      return new Logger(name, {
        logger: parentLogger.current,
        metadata: { ...logger.metadata, context: name, ...meta },
      });
    };

    return {
      logger,
      extend,
    };
  }, [isLoggerInput, loggerInput, parentLogger]);

  return (
    <LoggingContext.Provider value={context}>
      {children}
    </LoggingContext.Provider>
  );
};
